import { forwardRef, useEffect, useRef } from 'react';

import { bodyContainer } from './HiveTooltipItem.sty.js';
import { HiveTooltipItem } from './HiveTooltipItem.js';
import { useChartActionsMenu } from '../../../core/components/series-action/hooks/useChartActionsMenu.js';
import { mergeTooltipActions } from '../../../core/components/series-action/utils/mergeTooltipActions.js';
import { ChartTooltip } from '../../../core/components/tooltip/ChartTooltip/ChartTooltip.js';
import { useTooltipSeriesActions } from '../../../core/components/tooltip/shared/hooks/useTooltipSeriesActions.js';
import { safeGet } from '../../../core/utils/safe-get.js';
import { useHoneycombData } from '../../hooks/use-honeycomb-data.js';
import { useHoneycombTooltipMetadata } from '../../hooks/use-honeycomb-tooltip-metadata.js';
import { useHoneycombTruncationMode } from '../../hooks/use-honeycomb-truncation-mode.js';
import {
  useHoneycombOverlayMouseInBoundsState,
  useHoneycombTooltipPinnedState,
  useHoneycombTooltipState,
} from '../../store/selectors.js';
import { HoneycombTooltipMetadata } from '../../types/honeycomb-internal.js';
import { HoneycombTileData } from '../../types/honeycomb.js';

export const HiveTooltip = forwardRef<HTMLDivElement>((_, forwardedRef) => {
  const { enabled, visible, tileData, position } = useHoneycombTooltipState();
  const tooltipItem = useRef<HoneycombTooltipMetadata>({
    id: '',
    name: '',
    value: '',
    color: { Default: '' },
  });
  const pinned = useHoneycombTooltipPinnedState();
  const mouseInBounds = useHoneycombOverlayMouseInBoundsState();
  const { tooltipMetadata } = useHoneycombTooltipMetadata();
  const truncationMode = useHoneycombTruncationMode();
  const {
    selectedItem: selectedItemId,
    actionsMenuRef,
    updateSelectedItem,
    onLeave,
  } = useChartActionsMenu();
  const honeycombData = useHoneycombData();

  const seriesActions = useTooltipSeriesActions<HoneycombTileData>(
    honeycombData,
    (tileData) => safeGet(tooltipMetadata, tileData).id,
  );

  const honeycombActionsCallback = mergeTooltipActions(
    tooltipItem.current,
    seriesActions,
  );

  useEffect(() => {
    if (!pinned) {
      updateSelectedItem('');
    }
  }, [pinned, updateSelectedItem]);

  const forceHideTooltip = !pinned && (!mouseInBounds || !visible);

  if (!enabled || forceHideTooltip || !position) {
    return null;
  }

  if (!pinned && tileData) {
    // while tooltip is pinned, we don't update current tooltip information
    tooltipItem.current = safeGet(tooltipMetadata, tileData);
  }

  return (
    <ChartTooltip
      position={position}
      pinned={pinned}
      onMouseLeave={onLeave}
      ref={forwardedRef}
      actionSelectionValue={{
        selectedItemId,
        actionsMenuRef,
        updateSelectedItem,
      }}
    >
      <div className={bodyContainer}>
        <ChartTooltip.Body virtualization>
          <HiveTooltipItem
            color={tooltipItem.current.color.Default}
            name={tooltipItem.current.name}
            value={tooltipItem.current.value.toString()}
            actions={honeycombActionsCallback}
            truncationMode={truncationMode}
          />
        </ChartTooltip.Body>
      </div>
    </ChartTooltip>
  );
});

(HiveTooltip as typeof HiveTooltip & { displayName: string }).displayName =
  'HiveTooltip';
